import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Camera,
  CalendarMonth,
  ClearAll,
  Dashboard as DashboardIcon,
  DirectionsCar,
  EscalatorWarning,
  Face,
  FilterList,
  FormatListBulleted,
  Mood,
  NavigateBefore,
  NavigateNext,
  NotificationsActive,
  Refresh,
  Search,
  Settings,
  Shield,
} from '@mui/icons-material';
import logo from 'assets/images/logoProdutos/alertai_icon_light.svg';
import ImageModalComponent from 'components/ImageModalComponent';
import PanicAlarmCentralPopupComponent from 'components/PanicAlarmCentralPopupComponent';
import {
  EVENT_ENDPOINT,
  getAllStreamCountByLicenseAsync,
  getStreamByEndpointAsync,
} from 'services/Events';
import { EventDiscriminator } from 'services/Events/types';
import { transformDateStringInDate } from 'utils/TransformUtils';
import { useTranslation } from 'react-i18next';
import LastEvent from './_components/LastEvent';
import MonitoringOptionsComponent from './_components/MonitoringOptions';
import MonitoringOptionsNotificationComponent from './_components/MonitoringOptionsNotification';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:          '#0b1220',
  card:        '#111d32',
  head:        '#0d1a2f',
  panel:       '#0e1728',
  border:      'rgba(255,255,255,0.07)',
  rowHover:    'rgba(59,130,246,0.06)',
  rowSelected: 'rgba(59,130,246,0.13)',
  text:        '#e2e8f0',
  sub:         '#7a8699',
  accent:      '#3b82f6',
  input:       '#0d1a2f',
};

const noScrollbar = {
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth:   'none' as const,
  msOverflowStyle: 'none' as const,
};

const thSx = {
  background:    C.head,
  color:         C.sub,
  fontSize:      12,
  fontWeight:    700,
  borderColor:   C.border,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  py:            1.4,
  whiteSpace:    'nowrap' as const,
};

// ─── Type helpers ─────────────────────────────────────────────────────────────
const TYPE_OPTIONS: { label: string; value: string; color: string; bg: string; icon: JSX.Element }[] = [
  { label: 'Facial',      value: 'FaceRecognitionEventEntity',        color: '#34d399', bg: 'rgba(52,211,153,0.12)',   icon: <Mood sx={{ fontSize: 18 }} /> },
  { label: 'LPR',         value: 'LprEventEntity',                    color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',   icon: <DirectionsCar sx={{ fontSize: 18 }} /> },
  { label: 'IVS',         value: 'IvsEventEntity',                    color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',   icon: <Camera sx={{ fontSize: 18 }} /> },
  { label: 'Aluno',       value: 'StudentRecognitionEventEntity',     color: '#f472b6', bg: 'rgba(244,114,182,0.12)', icon: <Face sx={{ fontSize: 18 }} /> },
  { label: 'Responsável', value: 'ResponsibleRecognitionEventEntity', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', icon: <EscalatorWarning sx={{ fontSize: 18 }} /> },
];

const getTypeInfo = (discriminator: EventDiscriminator | string) => {
  const found = TYPE_OPTIONS.find(t => t.value === discriminator);
  return found ?? { icon: <Camera sx={{ fontSize: 18 }} />, color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', label: 'Evento' };
};

const EVENT_LASTEVENT_ENDPOINT = 'v1/events/last';
const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

// ─── Input styles helpers ─────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    background:   C.input,
    borderRadius: 1.5,
    fontSize:     13,
    color:        C.text,
    height:       34,
    '& fieldset':        { borderColor: C.border },
    '&:hover fieldset':  { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused fieldset': { borderColor: C.accent },
  },
  '& .MuiInputAdornment-root svg': { color: C.sub, fontSize: 18 },
  '& input': { py: 0 },
};

const dateSx = {
  '& .MuiOutlinedInput-root': {
    background:   C.input,
    borderRadius: 1.5,
    fontSize:     12,
    color:        C.text,
    height:       34,
    '& fieldset':             { borderColor: C.border },
    '&:hover fieldset':       { borderColor: 'rgba(255,255,255,0.2)' },
    '&.Mui-focused fieldset': { borderColor: C.accent },
  },
  '& input':                  { py: 0, colorScheme: 'dark', fontSize: 12 },
  '& .MuiInputAdornment-root svg': { color: C.sub, fontSize: 16 },
};

// ─── Component ────────────────────────────────────────────────────────────────
interface Filters {
  description: string;
  discriminator: string;
  startDate: string;
  endDate: string;
}

const EMPTY_FILTERS: Filters = { description: '', discriminator: '', startDate: '', endDate: '' };

const MonitoringPage = () => {
  const { t } = useTranslation();

  const [events, setEvents]               = useState<any[]>([]);
  const [lastEvent, setLastEvent]         = useState<any>();
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [totalCount, setTotalCount]       = useState(0);
  const [page, setPage]                   = useState(0);
  const [pageSize, setPageSize]           = useState(15);
  const [isLoading, setIsLoading]         = useState(false);
  const [filters, setFilters]   = useState<Filters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(true);

  const [isVisibleEditionDialog, setIsVisibleEditionDialog]           = useState(false);
  const [isVisibleNotificationDialog, setIsVisibleNotificationDialog] = useState(false);
  const [isOpenImageModal, setIsOpenImageModal] = useState(false);
  const [imageUrlToModal, setImageUrlToModal]   = useState('');

  const wsRef = useRef<WebSocket | null>(null);

  // ── Load events ──
  const loadEvents = useCallback(() => {
    if (wsRef.current) {
      (wsRef.current as any)._isIntentionalClose = true;
      wsRef.current.close();
    }

    getStreamByEndpointAsync(
      EVENT_ENDPOINT,
      setEvents,
      { pageNumber: page + 1, pageSize },
      undefined,   // onResponse
      setIsLoading,
      undefined,   // eventPauseUpdateParams
      true,        // isAllByLicense
    ).then(ws => { wsRef.current = ws; });

    getAllStreamCountByLicenseAsync(EVENT_ENDPOINT).then(setTotalCount);
  }, [page, pageSize]);

  useEffect(() => {
    loadEvents();
    return () => {
      if (wsRef.current) {
        (wsRef.current as any)._isIntentionalClose = true;
        wsRef.current.close();
      }
    };
  }, [loadEvents]);

  // ── Last event dedicated stream ──
  useEffect(() => {
    getStreamByEndpointAsync(EVENT_LASTEVENT_ENDPOINT, (evts: any[]) => {
      if (evts.length > 0) {
        const most = evts.reduce((a, b) =>
          transformDateStringInDate(b.createdAt) > transformDateStringInDate(a.createdAt) ? b : a
        );
        setLastEvent(most);
      }
    });
  }, []);

  useEffect(() => {
    if (events.length > 0 && !selectedEvent) {
      const most = events.reduce((a, b) =>
        transformDateStringInDate(b.createdAt) > transformDateStringInDate(a.createdAt) ? b : a
      );
      setLastEvent(most);
    }
  }, [events]);

  const handleRowClick = (ev: any) => {
    const next = selectedEvent?.id === ev.id ? null : ev;
    setSelectedEvent(next);
    setLastEvent(next ?? ev);
  };

  const handleClickImage = (url: string) => { setImageUrlToModal(url); setIsOpenImageModal(true); };

  const hasActiveFilters =
    !!filters.description || !!filters.discriminator || !!filters.startDate || !!filters.endDate;

  // ── Client-side filtering ──
  const filteredEvents = useMemo(() => {
    if (!hasActiveFilters) return events;
    return events.filter(ev => {
      if (filters.description) {
        const search = filters.description.toLowerCase();
        const desc   = (ev.description   || '').toLowerCase();
        const device = (ev.device?.name  || '').toLowerCase();
        if (!desc.includes(search) && !device.includes(search)) return false;
      }
      if (filters.discriminator && ev.discriminator !== filters.discriminator) return false;
      if (filters.startDate) {
        const evDate = new Date(ev.createdAt);
        const start  = new Date(filters.startDate);
        start.setHours(0, 0, 0, 0);
        if (evDate < start) return false;
      }
      if (filters.endDate) {
        const evDate = new Date(ev.createdAt);
        const end    = new Date(filters.endDate);
        end.setHours(23, 59, 59, 999);
        if (evDate > end) return false;
      }
      return true;
    });
  }, [events, filters.description, filters.discriminator, filters.startDate, filters.endDate, hasActiveFilters]);

  const clearFilters = () => { setFilters(EMPTY_FILTERS); };

  const setFilter = <K extends keyof Filters>(key: K, val: Filters[K]) =>
    setFilters(prev => ({ ...prev, [key]: val }));

  // Pagination
  const from       = totalCount > 0 ? page * pageSize + 1 : 0;
  const to         = totalCount > 0 ? Math.min((page + 1) * pageSize, totalCount) : events.length;
  const isLastPage = totalCount > 0 ? to >= totalCount : events.length < pageSize;

  return (
    <>
      <Box sx={{ display: 'flex', height: '100vh', width: '100%', background: C.bg, overflow: 'hidden' }}>

        {/* ═══════════════════ LEFT — Events table ═══════════════════ */}
        <Box sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          m: 2, mr: 1,
          background: C.card, borderRadius: 2, overflow: 'hidden', minWidth: 0,
        }}>

          {/* ── Header bar ── */}
          <Box sx={{
            px: 2.5, py: 1.5,
            display: 'flex', alignItems: 'center', gap: 1.5,
            background: C.head, borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}>
            <Typography fontWeight={700} fontSize={15} color={C.text}>Eventos</Typography>

            {totalCount > 0 && (
              <Box sx={{ px: 1, py: 0.25, borderRadius: 1, background: 'rgba(59,130,246,0.15)' }}>
                <Typography fontSize={12} color={C.accent} fontWeight={700}>
                  {totalCount.toLocaleString('pt-BR')}
                </Typography>
              </Box>
            )}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasActiveFilters && (
                <Tooltip title="Limpar todos os filtros">
                  <Box sx={{
                    px: 1, py: 0.25, borderRadius: 1,
                    background: 'rgba(251,191,36,0.12)',
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    cursor: 'pointer', mr: 0.5,
                    '&:hover': { background: 'rgba(251,191,36,0.2)' },
                  }} onClick={clearFilters}>
                    <ClearAll sx={{ fontSize: 14, color: '#fbbf24' }} />
                    <Typography fontSize={11} color="#fbbf24" fontWeight={700}>Filtros ativos</Typography>
                  </Box>
                </Tooltip>
              )}
              <Tooltip title={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}>
                <IconButton size="small" onClick={() => setShowFilters(s => !s)}
                  sx={{ color: showFilters ? C.accent : C.sub, '&:hover': { color: C.text } }}>
                  <FilterList sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Atualizar lista">
                <IconButton size="small" onClick={loadEvents}
                  sx={{ color: C.sub, '&:hover': { color: C.text } }}>
                  <Refresh sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* ── Filter bar ── */}
          {showFilters && (
            <Box sx={{
              background: C.panel,
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
            }}>
              {/* Row 1 — Search + type chips */}
              <Box sx={{
                px: 2, py: 1,
                display: 'flex', alignItems: 'center', gap: 1.5,
                borderBottom: `1px solid ${C.border}`,
                flexWrap: 'wrap',
              }}>
                <TextField
                  size="small"
                  placeholder="Buscar por descrição..."
                  value={filters.description}
                  onChange={e => setFilter('description', e.target.value)}
                  sx={{ ...inputSx, width: 230, flexShrink: 0 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><Search /></InputAdornment>
                    ),
                    endAdornment: filters.description ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setFilter('description', '')}
                          sx={{ color: C.sub, p: 0.25, lineHeight: 1 }}>✕</IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />

                {/* Tipo chips */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label="Todos"
                    size="small"
                    onClick={() => setFilter('discriminator', '')}
                    sx={{
                      fontSize: 12, height: 28,
                      color:      !filters.discriminator ? '#fff' : C.sub,
                      background: !filters.discriminator ? C.accent : 'rgba(255,255,255,0.06)',
                      fontWeight: !filters.discriminator ? 700 : 400,
                      cursor: 'pointer',
                      border: 'none',
                      '&:hover': { background: !filters.discriminator ? C.accent : 'rgba(255,255,255,0.1)' },
                    }}
                  />
                  {TYPE_OPTIONS.map(opt => {
                    const active = filters.discriminator === opt.value;
                    return (
                      <Chip
                        key={opt.value}
                        label={opt.label}
                        size="small"
                        onClick={() => setFilter('discriminator', active ? '' : opt.value)}
                        sx={{
                          fontSize: 12, height: 28,
                          color:      active ? opt.color : C.sub,
                          background: active ? opt.bg : 'rgba(255,255,255,0.06)',
                          fontWeight: active ? 700 : 400,
                          cursor: 'pointer',
                          border: active ? `1px solid ${opt.color}55` : 'none',
                          '&:hover': { background: opt.bg, color: opt.color },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Row 2 — Date range */}
              <Box sx={{
                px: 2, py: 1,
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarMonth sx={{ fontSize: 15, color: C.sub }} />
                  <Typography fontSize={12} color={C.sub}>Período:</Typography>
                </Box>

                <TextField
                  type="date"
                  size="small"
                  label="De"
                  value={filters.startDate}
                  onChange={e => setFilter('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true, style: { color: C.sub, fontSize: 11 } }}
                  sx={{ ...dateSx, width: 165 }}
                />
                <Typography fontSize={12} color={C.sub}>–</Typography>
                <TextField
                  type="date"
                  size="small"
                  label="Até"
                  value={filters.endDate}
                  onChange={e => setFilter('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true, style: { color: C.sub, fontSize: 11 } }}
                  inputProps={{ min: filters.startDate || undefined }}
                  sx={{ ...dateSx, width: 165 }}
                />

                {(filters.startDate || filters.endDate) && (
                  <Tooltip title="Limpar datas">
                    <IconButton size="small"
                      onClick={() => { setFilter('startDate', ''); setFilter('endDate', ''); }}
                      sx={{ color: C.sub, '&:hover': { color: '#fbbf24' } }}>
                      <ClearAll sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}

                {hasActiveFilters && (
                  <Box
                    onClick={clearFilters}
                    sx={{
                      ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 1.25, py: 0.4, borderRadius: 1, cursor: 'pointer',
                      background: 'rgba(251,191,36,0.1)',
                      border: '1px solid rgba(251,191,36,0.25)',
                      '&:hover': { background: 'rgba(251,191,36,0.18)' },
                    }}
                  >
                    <ClearAll sx={{ fontSize: 14, color: '#fbbf24' }} />
                    <Typography fontSize={11} color="#fbbf24" fontWeight={700}>Limpar filtros</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ── Table ── */}
          <TableContainer sx={{ flex: 1, overflowY: 'auto', ...noScrollbar }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 48, pl: 2 }}>Tipo</TableCell>
                  <TableCell sx={thSx}>Descrição</TableCell>
                  <TableCell sx={thSx}>Dispositivo</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'center', width: 70 }}>Imagem</TableCell>
                  <TableCell sx={{ ...thSx, width: 155 }}>Data</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  [...Array(Math.min(pageSize, 15))].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(5)].map((_, j) => (
                        <TableCell key={j} sx={{ borderColor: C.border, py: 1.2 }}>
                          <Skeleton variant="text" height={18} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredEvents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ borderColor: C.border, py: 6, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Search sx={{ fontSize: 36, color: C.sub, opacity: 0.4 }} />
                        <Typography fontSize={13} color={C.sub}>
                          {hasActiveFilters ? 'Nenhum evento encontrado para os filtros aplicados' : 'Nenhum evento encontrado'}
                        </Typography>
                        {hasActiveFilters && (
                          <Box
                            onClick={clearFilters}
                            sx={{
                              fontSize: 12, color: C.accent, cursor: 'pointer', textDecoration: 'underline',
                              '&:hover': { opacity: 0.75 },
                            }}
                          >
                            Limpar filtros
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEvents.map((ev, idx) => {
                    const typeInfo   = getTypeInfo(ev.discriminator);
                    const isSelected = selectedEvent?.id === ev.id;
                    const imgUrl     = ev.recognitionImage || ev.carImage || ev.image;
                    return (
                      <TableRow
                        key={ev.id ?? idx}
                        onClick={() => handleRowClick(ev)}
                        sx={{
                          cursor:     'pointer',
                          background: isSelected ? C.rowSelected : 'transparent',
                          borderLeft: isSelected ? `3px solid ${C.accent}` : '3px solid transparent',
                          transition: 'background 0.12s',
                          '&:hover': { background: isSelected ? C.rowSelected : C.rowHover },
                          '& td':    { borderColor: C.border },
                        }}
                      >
                        {/* Type icon */}
                        <TableCell sx={{ pl: isSelected ? 1.25 : 2 }}>
                          <Box sx={{ color: typeInfo.color, display: 'flex', alignItems: 'center' }}>
                            {typeInfo.icon}
                          </Box>
                        </TableCell>

                        {/* Description */}
                        <TableCell>
                          <Typography fontSize={13} color={C.text} noWrap sx={{ maxWidth: 340 }} title={ev.description}>
                            {ev.description || '—'}
                          </Typography>
                        </TableCell>

                        {/* Device */}
                        <TableCell>
                          <Typography fontSize={13} color={C.sub} noWrap sx={{ maxWidth: 180 }} title={ev.device?.name}>
                            {ev.device?.name || '—'}
                          </Typography>
                        </TableCell>

                        {/* Thumbnail */}
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          {imgUrl ? (
                            <Box
                              component="img"
                              src={imgUrl}
                              alt="event"
                              onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleClickImage(imgUrl); }}
                              sx={{
                                width: 48, height: 38, objectFit: 'cover',
                                borderRadius: 1, cursor: 'pointer',
                                border: `1px solid ${C.border}`,
                                display: 'block', mx: 'auto',
                                transition: 'opacity 0.15s',
                                '&:hover': { opacity: 0.75 },
                              }}
                            />
                          ) : (
                            <Box sx={{
                              width: 48, height: 38, borderRadius: 1,
                              background: C.panel, display: 'flex',
                              alignItems: 'center', justifyContent: 'center', mx: 'auto',
                              border: `1px solid ${C.border}`,
                            }}>
                              <Camera sx={{ fontSize: 16, color: C.sub }} />
                            </Box>
                          )}
                        </TableCell>

                        {/* Date */}
                        <TableCell>
                          <Typography fontSize={12} color={C.sub} noWrap>
                            {new Date(ev.createdAt).toLocaleString('pt-BR')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Pagination bar ── */}
          <Box sx={{
            px: 2.5, py: 1.1,
            display: 'flex', alignItems: 'center', gap: 1,
            borderTop: `1px solid ${C.border}`,
            flexShrink: 0,
          }}>
            <Typography fontSize={12} color={C.sub}>Quantidade por página:</Typography>
            <Select
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
              size="small"
              variant="standard"
              disableUnderline
              sx={{
                fontSize: 12, color: C.text, minWidth: 42,
                '& .MuiSelect-select': { py: 0, pr: '20px !important' },
                '& .MuiSelect-icon':   { color: C.sub, fontSize: 18 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: { background: '#1a2840', color: C.text, border: `1px solid ${C.border}` },
                },
              }}
            >
              {PAGE_SIZE_OPTIONS.map(s => (
                <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>
              ))}
            </Select>

            <Typography fontSize={12} color={C.sub} sx={{ ml: 1.5 }}>
              {totalCount > 0
                ? `${from.toLocaleString('pt-BR')}-${to.toLocaleString('pt-BR')} de ${totalCount.toLocaleString('pt-BR')}`
                : `${events.length} evento${events.length !== 1 ? 's' : ''}`}
            </Typography>

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Tooltip title="Página anterior">
                <span>
                  <IconButton size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}
                    sx={{ color: C.sub, '&:not(:disabled):hover': { color: C.text, background: 'rgba(255,255,255,0.05)' } }}>
                    <NavigateBefore sx={{ fontSize: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Box sx={{ px: 1.25, py: 0.25, borderRadius: 1, background: 'rgba(59,130,246,0.12)', minWidth: 28, textAlign: 'center' }}>
                <Typography fontSize={12} color={C.accent} fontWeight={700}>{page + 1}</Typography>
              </Box>
              <Tooltip title="Próxima página">
                <span>
                  <IconButton size="small" disabled={isLastPage} onClick={() => setPage(p => p + 1)}
                    sx={{ color: C.sub, '&:not(:disabled):hover': { color: C.text, background: 'rgba(255,255,255,0.05)' } }}>
                    <NavigateNext sx={{ fontSize: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* ═══════════════════ RIGHT — Logo + nav + detail ═══════════════════ */}
        <Box sx={{
          width: '35%', maxWidth: 440,
          m: 2, ml: 1,
          display: 'flex', flexDirection: 'column',
          background: C.card, borderRadius: 2,
          overflow: 'hidden', flexShrink: 0,
        }}>
          {/* Logo + nav */}
          <Box sx={{
            px: 2, pt: 2.5, pb: 2,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            background: C.head, borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}>
            <img src={logo} alt="alert ai" height={42} style={{ maxWidth: '100%' }} />

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
              {/*{[*/}
              {/*  { icon: <DashboardIcon sx={{ fontSize: 20 }} />,     label: 'Dashboard'   },*/}
              {/*  { icon: <FormatListBulleted sx={{ fontSize: 20 }} />, label: 'Lista'       },*/}
              {/*  { icon: <Shield sx={{ fontSize: 20 }} />,            label: 'Defesa'      },*/}
              {/*].map(item => (*/}
              {/*  <Tooltip key={item.label} title={item.label}>*/}
              {/*    <IconButton size="small"*/}
              {/*      sx={{ color: C.sub, borderRadius: 1.5, '&:hover': { background: 'rgba(255,255,255,0.06)', color: C.text } }}>*/}
              {/*      {item.icon}*/}
              {/*    </IconButton>*/}
              {/*  </Tooltip>*/}
              {/*))}*/}
              <Tooltip title="Notificações">
                <IconButton size="small" onClick={() => setIsVisibleNotificationDialog(true)}
                  sx={{ color: C.sub, borderRadius: 1.5, '&:hover': { background: 'rgba(255,255,255,0.06)', color: C.text } }}>
                  <NotificationsActive sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Configurações">
                <IconButton size="small" onClick={() => setIsVisibleEditionDialog(true)}
                  sx={{ color: C.sub, borderRadius: 1.5, '&:hover': { background: 'rgba(255,255,255,0.06)', color: C.text } }}>
                  <Settings sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Event detail */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <LastEvent
              event={selectedEvent ?? lastEvent}
              onClickInImage={handleClickImage}
              title={t('lastEvent')}
            />
          </Box>
        </Box>
      </Box>

      <MonitoringOptionsNotificationComponent
        isOpen={isVisibleNotificationDialog}
        setIsOpen={setIsVisibleNotificationDialog}
      />
      <MonitoringOptionsComponent
        isOpen={isVisibleEditionDialog}
        setIsOpen={setIsVisibleEditionDialog}
      />
      <ImageModalComponent
        isOpen={isOpenImageModal}
        handleClose={() => setIsOpenImageModal(false)}
        imageUrl={imageUrlToModal}
      />
      <PanicAlarmCentralPopupComponent />
    </>
  );
};

export default MonitoringPage;
